/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { getWhatsNew, markWhatsNewSeen, type WhatsNewResponse, type ReleaseNote } from '../lib/api'

interface WhatsNewContextType {
  unseenCount: number
  unseenReleases: ReleaseNote[]
  showDialog: boolean
  suppressDialog: boolean
  openDialog: () => void
  closeDialog: () => void
  refreshWhatsNew: () => Promise<void>
}

const WhatsNewContext = createContext<WhatsNewContextType | null>(null)

export function WhatsNewProvider({ children }: { children: ReactNode }) {
  const [unseenReleases, setUnseenReleases] = useState<ReleaseNote[]>([])
  const [unseenCount, setUnseenCount] = useState(0)
  const [showDialog, setShowDialog] = useState(false)
  const [suppressDialog, setSuppressDialog] = useState(false)
  const hasShownRef = useRef(false)
  const isClosingRef = useRef(false)

  const processResponse = useCallback((data: WhatsNewResponse) => {
    setUnseenReleases(data.unseen_releases)
    setUnseenCount(data.unseen_count)
    setSuppressDialog(data.suppress_dialog)

    // Auto-show dialog on first load if there are unseen releases and not suppressed
    if (data.unseen_count > 0 && !data.suppress_dialog && !hasShownRef.current) {
      setShowDialog(true)
      hasShownRef.current = true
    }
  }, [])

  useEffect(() => {
    getWhatsNew()
      .then(processResponse)
      .catch(() => {
        // Silently fail - What's New is non-critical
      })
  }, [processResponse])

  const closeDialog = useCallback(() => {
    // Guard against double-calling
    if (isClosingRef.current) return
    isClosingRef.current = true

    setShowDialog(false)

    // Mark the latest unseen release as seen (fire-and-forget)
    if (unseenReleases.length > 0) {
      const latestVersion = unseenReleases[0].version
      // Optimistically update count
      setUnseenCount(0)
      markWhatsNewSeen(latestVersion).catch(() => {
        // Silently fail - non-critical
      })
    }

    // Reset closing guard after a short delay
    setTimeout(() => {
      isClosingRef.current = false
    }, 100)
  }, [unseenReleases])

  const openDialog = useCallback(() => {
    if (unseenReleases.length > 0) {
      setShowDialog(true)
    }
  }, [unseenReleases])

  const refreshWhatsNew = useCallback(async () => {
    try {
      const data = await getWhatsNew()
      setUnseenReleases(data.unseen_releases)
      setUnseenCount(data.unseen_count)
      setSuppressDialog(data.suppress_dialog)
    } catch {
      // Silently fail
    }
  }, [])

  return (
    <WhatsNewContext.Provider value={{
      unseenCount,
      unseenReleases,
      showDialog,
      suppressDialog,
      openDialog,
      closeDialog,
      refreshWhatsNew,
    }}>
      {children}
    </WhatsNewContext.Provider>
  )
}

export function useWhatsNew() {
  const ctx = useContext(WhatsNewContext)
  if (!ctx) {
    throw new Error('useWhatsNew must be used within WhatsNewProvider')
  }
  return ctx
}
